import React, { useEffect, useRef, useState, useCallback } from 'react';

// Hook for scroll animations
export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (!options.repeat) {
            observer.unobserve(entry.target);
          }
        } else if (options.repeat) {
          setIsVisible(false);
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '0px',
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options.threshold, options.rootMargin, options.repeat]);

  return [ref, isVisible];
};

// Animated counter component
export const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollAnimation();
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      const startTime = Date.now();
      const startValue = 0;

      const animate = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.floor(startValue + (end - startValue) * easeOutQuart);
        
        setCount(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [isVisible, end, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Scroll animated container
export const ScrollReveal = ({ 
  children, 
  direction = 'up', 
  delay = 0,
  className = '' 
}) => {
  const [ref, isVisible] = useScrollAnimation();

  const getAnimationClass = () => {
    switch (direction) {
      case 'left':
        return 'animate-on-scroll-left';
      case 'right':
        return 'animate-on-scroll-right';
      default:
        return 'animate-on-scroll';
    }
  };

  return (
    <div
      ref={ref}
      className={`${getAnimationClass()} ${isVisible ? 'visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Staggered children animation
export const StaggerContainer = ({ children, staggerDelay = 100, className = '' }) => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, index) => (
        <div
          className={`animate-on-scroll ${isVisible ? 'visible' : ''}`}
          style={{ transitionDelay: `${index * staggerDelay}ms` }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

// Parallax effect hook
export const useParallax = (speed = 0.5) => {
  const [offset, setOffset] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const scrolled = window.scrollY;
        const elementTop = rect.top + scrolled;
        const relativeScroll = scrolled - elementTop + window.innerHeight;
        setOffset(relativeScroll * speed);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return [ref, offset];
};

// Confetti effect
export const useConfetti = () => {
  const triggerConfetti = useCallback(async () => {
    const confetti = (await import('canvas-confetti')).default;
    
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 9999,
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
      colors: ['#16a34a', '#22c55e', '#eab308'],
    });

    fire(0.2, {
      spread: 60,
      colors: ['#16a34a', '#22c55e', '#eab308'],
    });

    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
      colors: ['#16a34a', '#22c55e', '#eab308'],
    });

    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
      colors: ['#16a34a', '#22c55e', '#eab308'],
    });

    fire(0.1, {
      spread: 120,
      startVelocity: 45,
      colors: ['#16a34a', '#22c55e', '#eab308'],
    });
  }, []);

  return triggerConfetti;
};

// Credit counter animation
export const AnimatedCredits = ({ value, previousValue }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (previousValue !== undefined && value !== previousValue) {
      setIsAnimating(true);
      
      const diff = value - previousValue;
      const steps = Math.abs(diff);
      const duration = Math.min(1000, steps * 50);
      const stepTime = duration / steps;
      
      let current = previousValue;
      const interval = setInterval(() => {
        if (diff > 0) {
          current++;
        } else {
          current--;
        }
        setDisplayValue(current);
        
        if (current === value) {
          clearInterval(interval);
          setTimeout(() => setIsAnimating(false), 300);
        }
      }, stepTime);

      return () => clearInterval(interval);
    } else {
      setDisplayValue(value);
    }
  }, [value, previousValue]);

  return (
    <span className={`transition-all duration-300 ${isAnimating ? 'text-secondary scale-110' : ''}`}>
      {displayValue}
    </span>
  );
};

// Typing indicator component
export const TypingIndicator = () => (
  <div className="typing-indicator">
    <span />
    <span />
    <span />
  </div>
);

// Live activity notification
export const LiveActivityBanner = ({ activities }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (activities.length === 0) return;

    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % activities.length);
        setIsVisible(true);
      }, 300);
    }, 4000);

    return () => clearInterval(interval);
  }, [activities.length]);

  if (activities.length === 0) return null;

  const current = activities[currentIndex];

  return (
    <div
      className={`live-indicator transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
    >
      <span className="truncate max-w-[200px]">
        {current.user} {current.action}
      </span>
    </div>
  );
};

// Progress bar with animation
export const AnimatedProgress = ({ value, max = 100, className = '' }) => {
  const [width, setWidth] = useState(0);
  const [ref, isVisible] = useScrollAnimation();

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        setWidth((value / max) * 100);
      }, 100);
    }
  }, [isVisible, value, max]);

  return (
    <div ref={ref} className={`progress-bar ${className}`}>
      <div
        className="progress-bar-fill"
        style={{ width: `${width}%` }}
      />
    </div>
  );
};

// Avatar stack component
export const AvatarStack = ({ avatars, max = 5, size = 'md' }) => {
  const displayed = avatars.slice(0, max);
  const remaining = avatars.length - max;

  const sizeClasses = {
    sm: 'h-6 w-6 text-xs',
    md: 'h-8 w-8 text-sm',
    lg: 'h-10 w-10 text-base',
  };

  return (
    <div className="avatar-stack">
      {displayed.map((avatar, index) => (
        <div
          key={index}
          className={`${sizeClasses[size]} rounded-full bg-muted flex items-center justify-center overflow-hidden`}
          style={{ zIndex: displayed.length - index }}
        >
          {avatar.image ? (
            <img src={avatar.image} alt={avatar.name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-medium text-muted-foreground">
              {avatar.name?.charAt(0)}
            </span>
          )}
        </div>
      ))}
      {remaining > 0 && (
        <div
          className={`${sizeClasses[size]} rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium`}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
};

export default {
  useScrollAnimation,
  AnimatedCounter,
  ScrollReveal,
  StaggerContainer,
  useParallax,
  useConfetti,
  AnimatedCredits,
  TypingIndicator,
  LiveActivityBanner,
  AnimatedProgress,
  AvatarStack,
};
